/* REACT PACKAGE IMPORTS */
import { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";

/* INTERNAL IMPORTS */
import { openModal } from "../Features/Errors/Slices/errorSlice";
import { setSessionStatus } from "../Features/Sessions/Slices/sessionsSlice";
import "../styles/Assessment.css";
import "../styles/Session.css";

const useCountdown = (targetDate, paused) => {
  const countDownDate = new Date(targetDate).getTime();
  const [countDown, setCountDown] = useState(
    countDownDate - new Date().getTime()
  );

  useEffect(() => {
    const interval = setInterval(() => {
      if (!paused) setCountDown(countDownDate - new Date().getTime());
    }, 1000);

    return () => clearInterval(interval);
  }, [countDownDate, paused]);

  return getReturnValues(countDown);
};

const getReturnValues = (countDown) => {
  // calculate time left
  const hours = Math.floor(
    (countDown % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
  );
  const minutes = Math.floor((countDown % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((countDown % (1000 * 60)) / 1000);

  return [hours, minutes, seconds];
};

export default function CountdownTimer({
  targetDate,
  sessionExpired,
  setSessionExpired,
  paused,
  sessionStatus
}) {
  const [hours, minutes, seconds] = useCountdown(targetDate, paused);
  const mounted = useRef(null);
  const dispatch = useDispatch();

  useEffect(() => {
    mounted.current = true;
    if (mounted.current) {
      // Toast for 5 minutes of assessment time remaining
      if (hours === 0 && minutes === 5 && seconds === 0) {
        dispatch(
          openModal({
            message: "5 Minutes remaining",
            severity: "info",
          })
        );
      }
      // Set expired when time reaches 0
      setSessionExpired(hours + minutes + seconds <= 0);
      if (hours + minutes + seconds === 0) dispatch(setSessionStatus(sessionStatus))
    }
    return () => (mounted.current = false);
  }, [hours, minutes, seconds, sessionExpired, dispatch]);

  return <ShowCounter sessionExpired={sessionExpired} hours={hours} minutes={minutes} seconds={seconds} />;
}

const ShowCounter = ({ sessionExpired, hours, minutes, seconds }) => {
  const [countdownState, setCountdownState] = useState([]);

  const isDanger = countdownState === "danger";
  const dangerClass =
    "danger rounded-pill flex flex-row justify-center px-4 w-fit";
  const typicalClass = "bg-dark rounded-pill flex flex-row px-4 w-fit";

  return (
    <div className="w-full p-2 flex justify-center" elevation={6}>
      <div className={isDanger ? dangerClass : typicalClass}>
        <div className="flex flex-row justify-center items-center font-bold text-xl p-2 text-white w-fit gap-1">
          <div className="flex gap-1">
            {sessionExpired ? '--' : <DateTimeDisplay value={hours} type={"Hours"} />}
            <p className="m-0">:</p>
          </div>
          <div className="flex gap-1">
            {sessionExpired ? '--' : <DateTimeDisplay value={minutes} type={"Minutes"} />}
            <p className="m-0">:</p>
          </div>
          <div className="flex">
            {sessionExpired ? '--' : <DateTimeDisplay value={seconds} type={"Seconds"} />}
          </div>
        </div>
      </div>
    </div>
  );
};

const DateTimeDisplay = ({ value, type }) => {
  const formattedValue = value < 10 ? `0${value}` : value;
  return (
    <div className={"flex flex-col justify-center items-center"}>
      <p className="m-0">{formattedValue}</p>
      <span className="text-xs">{type}</span>
    </div>
  );
};
